import React, { useCallback, useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { VlSupportControl, VlSupportPanel } from './VlSupportPanel';
import { VlSupportThread } from '../SupportTickets/components/VlSupportThread';
import { subscribeVlSupportUpdates } from '../SupportTickets/hooks/useVlSupportAlerts';
import { completeVlSupportRequest } from './api/vlListing.api';
import { GET_VL_SUPPORT_REQUESTS_BY_TP_IDS } from './api/TicketsQuery';
import type { VlSupportEmailType, VlSupportEventDetails } from './vlSupportEmail';
import type { VlSupportRequest } from '../../types/ticket.types';

type Options = {
  /** Listings currently on screen. Their support requests are loaded together. */
  tpIds: string[];
  enabled: boolean;
  ownerInitials: string;
  onSent: (result: { sentTo?: string; redirectedForNonProd?: boolean; warning?: string }) => void;
  onCompleted: () => void;
  onError: (message: string) => void;
};

/**
 * The Victory Live support card controls (mail icon, "Open support ticket" badge, thread,
 * mark complete, composer) for any page that lists tickets. ManageTickets keeps its own copy
 * of this wiring; the user profile uses this so its Tickets tab behaves the same.
 */
export function useVlSupportCards({ tpIds, enabled, ownerInitials, onSent, onCompleted, onError }: Options) {
  const apolloClient = useApolloClient();
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [thread, setThread] = useState<{ rowKey: string; ticketId: string } | null>(null);
  const [completingKey, setCompletingKey] = useState<string | null>(null);
  const [byTpId, setByTpId] = useState<Record<string, VlSupportRequest[]>>({});
  const [available, setAvailable] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);
  useEffect(() => subscribeVlSupportUpdates(reload), [reload]);

  const tpIdsKey = tpIds.join(',');
  useEffect(() => {
    if (!enabled || !available || !tpIds.length) return;
    let cancelled = false;
    (async () => {
      try {
        const next: Record<string, VlSupportRequest[]> = {};
        for (let i = 0; i < tpIds.length; i += 400) {
          const { data } = await apolloClient.query({
            query: GET_VL_SUPPORT_REQUESTS_BY_TP_IDS,
            variables: { tpIds: tpIds.slice(i, i + 400) },
            fetchPolicy: 'network-only',
          });
          for (const row of data?.vl_support_request || []) (next[row.tp_id] ||= []).push(row);
        }
        if (!cancelled) setByTpId(next);
      } catch (err) {
        console.warn('Victory Live support requests unavailable on this Hasura', err);
        if (!cancelled) setAvailable(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, available, tpIdsKey, reloadToken, apolloClient]);

  const withRequests = (ids: string[]) => ids.map((id) => ({ tp_id: id, vl_support_requests: byTpId[id] ?? [] }));

  const complete = async (rowKey: string, ids: string[]) => {
    if (completingKey) return;
    setCompletingKey(rowKey);
    try {
      await completeVlSupportRequest(ids);
      onCompleted();
      reload();
    } catch (error: any) {
      onError(error?.message || 'Failed to mark Victory Live support as completed');
    } finally {
      setCompletingKey(null);
    }
  };

  /** Mail icon, status badge and thread/complete buttons for one card. */
  const renderControl = (rowKey: string, ids: string[]) => {
    if (!enabled || !ids.length) return null;
    return (
      <VlSupportControl
        tickets={withRequests(ids)}
        open={openKey === rowKey}
        busy={completingKey === rowKey}
        onToggle={() => {
          setThread(null);
          setOpenKey((prev) => (prev === rowKey ? null : rowKey));
        }}
        onComplete={() => void complete(rowKey, ids)}
        threadOpen={thread?.rowKey === rowKey}
        onOpenThread={(ticketId) => {
          setOpenKey(null);
          setThread((prev) => (prev?.rowKey === rowKey ? null : { rowKey, ticketId }));
        }}
      />
    );
  };

  /** The row that expands under a card: the Victory Live thread, or the email composer. */
  const renderPanelRow = (
    rowKey: string,
    ids: string[],
    details: VlSupportEventDetails,
    colSpan: number,
    expandedRowClassName?: string,
    lockedType?: VlSupportEmailType,
  ) => {
    if (!enabled || !ids.length) return null;
    if (thread?.rowKey === rowKey) {
      return (
        <tr key={`vl-thread-${rowKey}`} className={expandedRowClassName}>
          <td colSpan={colSpan}>
            <div style={{ maxWidth: 760, margin: '8px 0', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px' }}>
              <VlSupportThread ticketId={thread.ticketId} onClose={() => setThread(null)} onChanged={reload} />
            </div>
          </td>
        </tr>
      );
    }
    if (openKey !== rowKey) return null;
    return (
      <tr key={`vl-support-${rowKey}`} className={expandedRowClassName}>
        <td colSpan={colSpan}>
          <VlSupportPanel
            tpIds={ids}
            details={details}
            trackingAvailable={available}
            ownerInitials={ownerInitials}
            lockedType={lockedType}
            onClose={() => setOpenKey(null)}
            onError={() => undefined}
            onSent={(result) => {
              onSent(result);
              setOpenKey(null);
              reload();
            }}
          />
        </td>
      </tr>
    );
  };

  return { renderControl, renderPanelRow, available };
}
